import { type Tag } from "~/components/planning/Tags/TagBadge"
import { Card, CardContent } from "../../Card"
import { type CustomTooltipProps } from "ag-grid-react"
import HeaderTooltip from "./HeaderTooltip"
import { filterDefined, groupBy, uniq } from "~/utils/tsUtils"
import { useGetTags } from "~/hooks/useTags"
import { useMemo } from "react"
import { type IVariantSummary } from "~/hooks/useVariantTableData"
import { TagBadgeWithRemove } from "../../TagBadgeWithRemove"
import { IconButton } from "../../Button"
import { PencilSimple } from "@phosphor-icons/react"
import { getEntriesWithTag, useTagActions } from "~/hooks/useTagActions"
import { ConfirmationDialog } from "../../ConfirmationDialog"
import React from "react"
import { Badge } from "../../Badge"
import { Separator } from "../../Separator"

type TagsTooltipValue = {
  entries: IVariantSummary[]
  tags: Tag[]
  onEditClick?: () => void
}

// Module-level map to store edit click callbacks keyed by sorted skuIds
const editClickCallbacks = new Map<string, () => void>()

export function setEditClickCallback(key: string, callback: () => void) {
  editClickCallbacks.set(key, callback)
}

export function getEditClickCallback(key: string): (() => void) | undefined {
  return editClickCallbacks.get(key)
}

export default function TagsTooltip(props: CustomTooltipProps<IVariantSummary, TagsTooltipValue>) {
  const { node, value } = props
  const skuIds = useMemo(() => {
    if (value?.entries && Array.isArray(value.entries)) {
      return value.entries.flatMap((entry) => entry.skuIds)
    }
    return (
      (node?.group
        ? filterDefined(node?.allLeafChildren?.flatMap((node) => node.data?.skuIds) ?? [])
        : node?.data?.skuIds) ?? []
    )
  }, [node, value])

  const tags = value?.tags ?? []
  const entries = value?.entries ?? []

  const { tagIsFiltered, filterOnClick, onRemoveTag, selectedTag, setSelectedTag, canEdit } =
    useTagActions({ skuIds })

  // Removal is scoped to the variants that actually carry the selected tag.
  const selectedTagEntries = getEntriesWithTag(entries, selectedTag)
  const selectedTagSkuIds = uniq(selectedTagEntries.flatMap((entry) => entry.skuIds))

  const dialogKey = useMemo(() => skuIds.sort().join(","), [skuIds])
  const { isPending: isLoadingTags } = useGetTags()

  const tagGroups = filterDefined(entries.flatMap((entry) => entry.tags ?? []))
  const groupedTags = useMemo(() => groupBy(tagGroups, (tag) => tag.id), [tagGroups])

  // If the hovered cell is the header
  if (props.location === "header") return <HeaderTooltip {...props} />

  // Don't show tooltip if there are no tags
  if (!tags || tags.length === 0) return null

  const handleEditClick = () => {
    const callback = value?.onEditClick || getEditClickCallback(dialogKey)
    callback?.()
  }

  return (
    <>
      <div className="bg-base w-fit max-w-[90vw] mr-6 z-10 relative">
        <Card className="w-full">
          <CardContent className="max-h-[400px] overflow-y-auto p-2">
            <div className="flex flex-col gap-2">
              <div className="flex flex-row justify-between items-center">
                <span className="label-base">
                  Tags <p className="text-sm text-fg-muted">Click to filter by tag</p>
                </span>
                {value?.onEditClick && (
                  <IconButton
                    variant="neutral"
                    size="xs"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleEditClick()
                    }}
                  >
                    <PencilSimple />
                  </IconButton>
                )}
              </div>
              <Separator orientation="horizontal" />
              <div className="flex flex-col gap-1.5 flex-wrap">
                {isLoadingTags ? (
                  <span className="text-sm text-muted-foreground">Loading tags…</span>
                ) : tags?.length === 0 ? (
                  <span className="text-sm text-muted-foreground">No tags available</span>
                ) : (
                  <>
                    {tags?.map((tag) => {
                      const groupedTagCount = groupedTags?.[tag.id]?.length
                      return (
                        <div key={tag.id} className="flex items-center">
                          <span>
                            There are{" "}
                            <Badge variant="neutral" size="xs">
                              {groupedTagCount}
                            </Badge>{" "}
                            variants with the tag{" "}
                            <TagBadgeWithRemove
                              tag={tag}
                              isActive={tagIsFiltered(tag)}
                              canEdit={canEdit}
                              onFilterClick={filterOnClick}
                              onRemove={setSelectedTag}
                              tooltipTitle="Remove tag from SKUs"
                              className="max-h-none h-auto [&_span]:max-w-none [&_span]:whitespace-normal"
                            />
                          </span>
                        </div>
                      )
                    })}
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <ConfirmationDialog
        title={`Remove tag '${selectedTag?.name}'`}
        description={`Are you sure you want to remove the tag '${selectedTag?.name}' affecting ${selectedTagSkuIds.length} SKUs?`}
        onConfirm={() => {
          if (selectedTag) {
            onRemoveTag(selectedTag, selectedTagSkuIds, selectedTagEntries.length)
          }
        }}
        onCancel={() => setSelectedTag(undefined)}
        open={!!selectedTag?.id}
        disabled={!canEdit}
      />
    </>
  )
}
